import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { ArrowRight, Check, Sparkles } from 'lucide-react';
import { defaultDomains } from '../mock';

const HomePage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-white">
      {/* Hero Section */}
      <section className="pt-20 pb-32 px-4">
        <div className="max-w-6xl mx-auto text-center">
          <div className="inline-flex items-center space-x-2 bg-white px-4 py-2 rounded-full shadow-sm mb-8 border border-green-200">
            <Sparkles className="w-4 h-4 text-green-600" />
            <span className="text-sm font-medium text-green-800">Premium Version Available</span>
          </div>
          
          {/* Centered Logo with Bounce Animation */}
          <div className="flex justify-center mb-8">
            <img 
              src="https://customer-assets.emergentagent.com/job_balance-wheel-app/artifacts/duaxaily_Life%20Worth%20Funding%20Logo%20Transparente.png" 
              alt="Life Worth Funding" 
              className="h-32 animate-bounce-slow"
            />
          </div>
          
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
            A life worth living is a life <br />
            <span className="text-green-600">worth funding</span>
          </h1>
          
          <p className="text-xl text-gray-600 mb-10 max-w-3xl mx-auto">
            Get instant clarity on how your life is aligned—and where it's asking for more.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16">
            <Link to="/assessment">
              <Button size="lg" className="bg-green-600 hover:bg-green-700 text-white px-8 py-6 text-lg shadow-lg hover:shadow-xl transition-all">
                Take Free Assessment
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Link to="/customize">
              <Button size="lg" variant="outline" className="border-2 border-green-600 text-green-600 hover:bg-green-50 px-8 py-6 text-lg">
                Customize Your Wheel
              </Button>
            </Link>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-2xl shadow-2xl p-8">
              <img 
                src="https://wheeloflife.io/wheel-of-life-homepage-320px.png" 
                alt="Wheel of Life" 
                className="w-full max-w-md mx-auto"
              />
            </div>
          </div>
        </div>
      </section>

      {/* What is Wheel of Life */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-4xl font-bold text-center text-gray-900 mb-6">
            What is the Life Wheel?
          </h2>
          <p className="text-lg text-gray-600 text-center mb-16 max-w-3xl mx-auto">
            The Life Wheel helps you take a panoramic view of your life and get clarity to move forward.
            Use this free assessment to spotlight your strengths, uncover blind-spots, and kick-start 
            an action plan that moves you forward.
          </p>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="p-6 border-2 border-green-100 hover:border-green-300 transition-all hover:shadow-lg">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <Check className="w-6 h-6 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-gray-900">Spotlight Strengths</h3>
              <p className="text-gray-600">
                Identify the areas of your life where you're thriving and feeling fulfilled.
              </p>
            </Card>

            <Card className="p-6 border-2 border-green-100 hover:border-green-300 transition-all hover:shadow-lg">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <Check className="w-6 h-6 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-gray-900">Uncover Blind-Spots</h3>
              <p className="text-gray-600">
                Discover areas that need attention and could benefit from more focus.
              </p>
            </Card>

            <Card className="p-6 border-2 border-green-100 hover:border-green-300 transition-all hover:shadow-lg">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <Check className="w-6 h-6 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-gray-900">Action Plan</h3>
              <p className="text-gray-600">
                Create concrete steps to move forward and improve your life balance.
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* Life Domains */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold text-center text-gray-900 mb-16">
            Twelve Life Domains
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {defaultDomains.map((domain) => (
              <Card
                key={domain.id}
                className="p-6 transition-all hover:shadow-lg cursor-pointer border-2"
                style={{ 
                  borderColor: domain.color + '40',
                  backgroundColor: domain.color + '08'
                }}
              >
                <div className="flex items-center space-x-3 mb-4">
                  <div 
                    className="w-4 h-4 rounded-full flex-shrink-0"
                    style={{ backgroundColor: domain.color }}
                  />
                  <h3 className="font-bold text-gray-900 text-lg">{domain.name}</h3>
                </div>
                <div className="space-y-2">
                  <p className="text-sm font-semibold text-gray-700 mb-2">Reflect on:</p>
                  {domain.questions.map((question, idx) => (
                    <div key={idx} className="flex items-start space-x-2">
                      <span className="text-xs font-bold text-gray-500 mt-0.5">{idx + 1}.</span>
                      <p className="text-sm text-gray-600 italic">{question}</p>
                    </div>
                  ))}
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-r from-green-600 to-green-700">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Ready to transform your life?
          </h2>
          <p className="text-xl text-green-100 mb-10">
            Start your free assessment now and get instant clarity on your life balance.
          </p>
          <Link to="/assessment">
            <Button size="lg" className="bg-white text-green-600 hover:bg-gray-100 px-8 py-6 text-lg shadow-xl">
              Get Started Free
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
